"use client";

import { useMutation } from "@tanstack/react-query";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import { featureAnnouncementsUserEq, FeatureAnnouncementsUserRpc } from "@/lib/effect-query";

// ============================================================================
// UI-to-Domain Mapping
// ============================================================================

type UiMood = "love" | "good" | "okay" | "bad";
type UiCategory = "bug" | "feature" | "general" | "other" | "praise" | "usability";
function mapMood(uiMood: UiMood): "positive" | "neutral" | "negative" {
  switch (uiMood) {
    case "love":
    case "good":
      return "positive";
    case "okay":
      return "neutral";
    case "bad":
      return "negative";
  }
}
function mapCategory(uiCategory: UiCategory): "general" | "bug_report" | "feature_request" | "usability" {
  switch (uiCategory) {
    case "bug":
      return "bug_report";
    case "feature":
      return "feature_request";
    case "general":
      return "general";
    case "other":
      return "general";
    case "praise":
      return "general";
    case "usability":
      return "usability";
  }
}

// ============================================================================
// Input Type
// ============================================================================

export interface SubmitFeedbackInput {
  readonly featureKey: string;
  /** The page the user was actually on (route key), for the "Submitted from" line. */
  readonly originFeatureKey?: string | null;
  readonly mood: UiMood;
  readonly category?: UiCategory | null;
  readonly impactTags: readonly string[];
  readonly comment?: string;
  readonly hasScreenshots: boolean;
  readonly usageFrequency?: "daily" | "weekly" | "first_time" | "rarely";
  readonly followupConsent?: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function useSubmitFeedback() {
  const mutation = useMutation(featureAnnouncementsUserEq.mutationOptions({
    mutationKey: ["feature-announcements", "submit-feedback"],
    mutationFn: (input: SubmitFeedbackInput) => Effect.gen(function* () {
      const rpcPayload = {
        featureKey: input.featureKey,
        originFeatureKey: input.originFeatureKey ?? undefined,
        mood: mapMood(input.mood),
        category: input.category ? mapCategory(input.category) : undefined,
        impactTags: Array.from(input.impactTags),
        comment: input.comment || undefined,
        hasScreenshots: input.hasScreenshots,
        usageFrequency: input.usageFrequency,
        followupConsent: input.followupConsent ?? false
      };
      console.log("[useSubmitFeedback] Mapped RPC payload:", rpcPayload);
      const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc);
      const result = yield* client.submitFeedback(rpcPayload);
      console.log("[useSubmitFeedback] RPC response:", result);
      return result;
    }).pipe(Effect.scoped)
  }));
  return {
    submitFeedback: mutation.mutateAsync,
    isSubmitting: mutation.isPending,
    error: mutation.error
  };
}